import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../../components/utils";
import {
  CheckBoxWithLabel,
  Input,
} from "../../../components/shared/InputField";
import {
  CampaignRow,
  saveCustomCampaignTemplate,
  sendCustomEmailCampaign,
  sendTestEmailCampaign,
} from "../../../api/customEmail";
import { getEmailSettings } from "../../../api/siteSettings";
import { CampaignState } from "./types";
import { buildPreviewHtml } from "./helpers";

interface Props {
  state: CampaignState;
  onBack: () => void;
  onSendSuccess?: () => void;
}

export default function Step5Preview({ state, onBack, onSendSuccess }: Props) {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [testHrId, setTestHrId] = useState("");
  const [testAdminEmail, setTestAdminEmail] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [emailToggleOff, setEmailToggleOff] = useState(false);
  const [overrideEmailToggle, setOverrideEmailToggle] = useState(false);

  const previewHtml = buildPreviewHtml(state.htmlContent);

  useEffect(() => {
    getEmailSettings()
      .then((settings) => {
        setEmailToggleOff(!settings.emailsToBeSent);
      })
      .catch(() => {});
  }, []);

  const handleTestEmail = async () => {
    if (!testHrId.trim()) {
      toast.error("Enter an HR ID to test with");
      return;
    }
    if (!testAdminEmail.trim()) {
      toast.error("Enter your email address to receive the test");
      return;
    }
    setTestSending(true);
    try {
      const matchingRow =
        state.dataRows.find((r) => r.employeeHrId === testHrId.trim()) ??
        state.dataRows[0];
      await sendTestEmailCampaign(
        state.htmlContent,
        state.subject,
        testHrId.trim(),
        testAdminEmail.trim(),
        matchingRow?.customVars ?? {}
      );
      toast.success("Test email sent! Check your inbox.");
    } catch {
      toast.error("Failed to send test email.");
    } finally {
      setTestSending(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const rows: CampaignRow[] = state.dataRows.map((r) => ({
        employeeId: r.employeeId,
        employeeHrId: r.employeeHrId,
        customVars: r.customVars,
      }));
      const result = await sendCustomEmailCampaign(
        state.htmlContent,
        state.subject,
        rows
      );
      toast.success(
        `Email sent to ${result.sent} employee${
          result.sent !== 1 ? "s" : ""
        } successfully.`
      );
      onSendSuccess?.();
      navigate("/options/allEmployees");
    } catch {
      toast.error("Failed to send emails. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleSave = async () => {
    if (!saveName.trim()) {
      toast.error("Enter a template name to save");
      return;
    }
    setSaving(true);
    try {
      await saveCustomCampaignTemplate(
        saveName.trim(),
        state.htmlContent,
        state.subject
      );
      toast.success("Template saved successfully");
    } catch {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <VStack className="gap-4 p-6 h-full">
      <HStack className="items-center justify-between">
        <p className="text-sm font-medium text-gray-700">
          Template preview (variables will be substituted when sending):
        </p>
        <span className="text-xs text-gray-500">
          {state.dataRows.length > 0
            ? `${state.dataRows.length} recipient(s)`
            : "All active employees"}
        </span>
      </HStack>

      <Box className="border border-gray-200 rounded-lg overflow-hidden flex-1 min-h-[500px]">
        <iframe
          title="Email Preview"
          srcDoc={previewHtml}
          className="w-full h-full min-h-[500px] bg-white"
          sandbox="allow-same-origin"
        />
      </Box>

      {/* Test email */}
      <VStack className="gap-3 p-4 border border-blue-100 rounded-lg bg-blue-50">
        <HStack className="items-center gap-2">
          <Icon
            icon="mdi:email-send-outline"
            className="text-blue-600"
            height={18}
          />
          <p className="text-sm font-semibold text-blue-700">
            Send Test Email
            <span className="ml-1 font-normal text-blue-400 text-xs">
              (optional)
            </span>
          </p>
        </HStack>
        <HStack className="gap-3 items-end">
          <VStack className="flex-1 gap-1">
            <label className="text-xs text-gray-500 font-medium">
              Employee HR ID
            </label>
            <Input
              className="w-full px-3 py-2 text-sm"
              value={testHrId}
              onChange={(e) => setTestHrId(e.target.value)}
              placeholder="e.g. EMP001"
            />
          </VStack>
          <VStack className="flex-1 gap-1">
            <label className="text-xs text-gray-500 font-medium">
              Your email
            </label>
            <Input
              className="w-full px-3 py-2 text-sm"
              value={testAdminEmail}
              onChange={(e) => setTestAdminEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </VStack>
          <ButtonSecondary
            className="whitespace-nowrap flex items-center gap-1.5 px-4"
            onClick={handleTestEmail}
            loading={testSending}
            disabled={testSending}
          >
            <Icon icon="mdi:send-outline" height={15} />
            Send Test
          </ButtonSecondary>
        </HStack>
        <p className="text-xs text-blue-500">
          Sends the email with that employee&apos;s data substituted, delivered
          to your inbox with a <span className="font-semibold">[TEST]</span>{" "}
          subject prefix.
        </p>
      </VStack>

      {/* Save template */}
      <VStack className="gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <HStack className="items-center gap-2">
          <Icon
            icon="mdi:content-save-outline"
            className="text-gray-500"
            height={18}
          />
          <p className="text-sm font-semibold text-gray-700">
            Save Template
            <span className="ml-1 font-normal text-gray-400 text-xs">
              (optional)
            </span>
          </p>
        </HStack>
        <HStack className="gap-3 items-center">
          <Input
            className="flex-1 px-3 py-2 text-sm"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="Template name for reuse..."
          />
          <ButtonSecondary
            className="whitespace-nowrap flex items-center gap-1.5 px-4"
            onClick={handleSave}
            loading={saving}
            disabled={saving}
          >
            <Icon icon="mdi:content-save-outline" height={15} />
            Save Template
          </ButtonSecondary>
        </HStack>
        <p className="text-xs text-gray-400">
          Saved templates can be loaded in future sends.
        </p>
      </VStack>

      {emailToggleOff && (
        <VStack className="gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <HStack className="items-center gap-2">
            <Icon
              icon="mdi:alert-circle-outline"
              className="text-orange-500 shrink-0"
              height={18}
            />
            <p className="text-sm font-semibold text-gray-700">
              Email toggle is currently OFF
            </p>
          </HStack>
          <p className="text-xs text-gray-400">
            Emails to employees are disabled in your settings. Check the box
            below to send anyway.
          </p>
          <CheckBoxWithLabel
            label="Override and send emails anyway"
            labelclassname="ml-2 text-xs text-gray-500"
            checked={overrideEmailToggle}
            onChange={(e) =>
              setOverrideEmailToggle((e.target as HTMLInputElement).checked)
            }
            className="w-3.5 h-3.5 cursor-pointer"
          />
        </VStack>
      )}

      <HStack className="flex-row-reverse gap-3 mt-auto">
        <ButtonPrimary
          onClick={handleSend}
          loading={sending}
          disabled={emailToggleOff && !overrideEmailToggle}
          title={
            emailToggleOff && !overrideEmailToggle
              ? "Email master toggle is off. Check the override box above to send anyway."
              : undefined
          }
        >
          Send Emails
        </ButtonPrimary>
        <ButtonSecondary onClick={onBack} disabled={sending}>
          Back
        </ButtonSecondary>
      </HStack>
    </VStack>
  );
}
